/**
 * Admin Routes
 * Routes for system administration, user management, and settings
 */

import { lazy } from "react";
import { RouteObject } from "react-router-dom";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { AppShell } from "@/components/layout/AppShell";
import { LazyPage, RedirectKeepingState } from "../helpers";
import { NOTIFICATION_SETTINGS_ROLES } from "@/pages/settings/notificationSettingsRows";

// Dashboard - Load immediately (most visited)
import { DashboardPage } from "@/pages/DashboardPage";
import { ProfilePage } from "@/pages/ProfilePage";

// Lazy loaded pages
const UsersListPage = lazy(() => import("@/pages/users/UsersListPage").then(m => ({ default: m.UsersListPage })));
const UserFormPage = lazy(() => import("@/pages/users/UserFormPage").then(m => ({ default: m.UserFormPage })));
const UserDetailPage = lazy(() => import("@/pages/users/UserDetailPage"));
const ChangePasswordPage = lazy(() => import("@/pages/auth/ChangePasswordPage"));
const TenantSettingsPage = lazy(() => import("@/pages/tenants/TenantSettingsPage").then(m => ({ default: m.TenantSettingsPage })));
const AuditLogPage = lazy(() => import("@/pages/admin/AuditLogPage"));
const NotificationSettingsPage = lazy(() => import("@/pages/settings/NotificationSettingsPage"));

export const adminRoutes: RouteObject[] = [
  // Dashboard
  {
    path: "/dashboard",
    element: (
      // The school dashboard is built on the admin metrics API (admin and
      // principal only, financial data). Anyone else who lands here — staff
      // by habit or an old bookmark — goes to their own home instead of an
      // "Access Restricted" page.
      <ProtectedRoute permission="view:school-metrics" deniedRedirect="home">
        <AppShell>
          <DashboardPage />
        </AppShell>
      </ProtectedRoute>
    ),
  },

  // Profile & Settings
  {
    path: "/profile",
    element: (
      <ProtectedRoute>
        <AppShell>
          <ProfilePage />
        </AppShell>
      </ProtectedRoute>
    ),
  },
  {
    path: "/change-password",
    element: (
      <ProtectedRoute>
        <AppShell>
          <LazyPage><ChangePasswordPage /></LazyPage>
        </AppShell>
      </ProtectedRoute>
    ),
  },
  {
    // Every role the notifications preferences API serves (not super_user,
    // who has no tenant). Linked from Profile and the account menu.
    path: "/settings/notifications",
    element: (
      <ProtectedRoute roles={[...NOTIFICATION_SETTINGS_ROLES]}>
        <AppShell>
          <LazyPage><NotificationSettingsPage /></LazyPage>
        </AppShell>
      </ProtectedRoute>
    ),
  },

  // User Management
  {
    path: "/users",
    element: (
      <ProtectedRoute permission="manage:users">
        <AppShell>
          <LazyPage><UsersListPage /></LazyPage>
        </AppShell>
      </ProtectedRoute>
    ),
  },
  {
    path: "/users/new",
    element: (
      <ProtectedRoute permission="manage:users">
        <AppShell>
          <LazyPage><UserFormPage /></LazyPage>
        </AppShell>
      </ProtectedRoute>
    ),
  },
  {
    path: "/users/:userId/edit",
    element: (
      <ProtectedRoute permission="manage:users">
        <AppShell>
          <LazyPage><UserFormPage /></LazyPage>
        </AppShell>
      </ProtectedRoute>
    ),
  },
  {
    path: "/users/:userId",
    element: (
      <ProtectedRoute permission="manage:users">
        <AppShell>
          <LazyPage><UserDetailPage /></LazyPage>
        </AppShell>
      </ProtectedRoute>
    ),
  },

  // Admin Settings
  {
    path: "/admin/tenant-settings",
    element: (
      <ProtectedRoute roles={["super_user", "admin"]}>
        <AppShell>
          <LazyPage><TenantSettingsPage /></LazyPage>
        </AppShell>
      </ProtectedRoute>
    ),
  },
  {
    path: "/admin/audit-log",
    element: (
      <ProtectedRoute permission="view:audit-log">
        <AppShell>
          <LazyPage><AuditLogPage /></LazyPage>
        </AppShell>
      </ProtectedRoute>
    ),
  },
  {
    // Redirect old stripe-config route to tenant settings
    path: "/admin/stripe-config",
    element: <RedirectKeepingState to="/admin/tenant-settings" />,
  },
];
